import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Clock, Bell, Ban, Trash2, Sparkles, Users, User, Building2, Plus, X } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Card, Badge, Btn, Input } from "@/components/kit";

const KIND = {
  meeting: { label: "Reunião", icon: Users, tone: "primary" },
  reminder: { label: "Lembrete", icon: Bell, tone: "muted" },
  task: { label: "Tarefa", icon: Bell, tone: "muted" },
  block: { label: "Bloqueio", icon: Ban, tone: "muted" },
  followup: { label: "Follow-up", icon: Sparkles, tone: "primary" },
  sector_meeting: { label: "Reunião de setor", icon: Building2, tone: "primary" },
};

const TZ = "America/Sao_Paulo";
const fmt = (iso) => iso ? new Date(iso).toLocaleString("pt-BR", { timeZone: TZ, weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "Sem horário";
const dayKey = (iso) => iso ? new Date(iso).toLocaleDateString("pt-BR", { timeZone: TZ, weekday: "long", day: "2-digit", month: "long" }) : "Sem data";
const firstName = (n) => (n || "").split(" ")[0];

function CreateModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) return toast.error("Informe o título da reunião");
    if (!date || !time) return toast.error("Informe a data e a hora");
    setSaving(true);
    try {
      // send a BRT (-03:00) ISO so the stored wall-clock time matches what the user picked
      const start = `${date}T${time}:00-03:00`;
      await api.post("/isa/sector-meetings", { title: title.trim(), sector: "commercial", start });
      toast.success("Reunião do setor agendada — o time será avisado 1 dia e 1 hora antes.");
      onCreated();
      onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-24 backdrop-blur-sm" data-testid="sector-meeting-modal">
      <Card className="w-full max-w-lg p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600"><Building2 size={18} /></span>
            <div>
              <h2 className="font-display text-lg font-semibold text-zinc-900">Nova reunião do setor</h2>
              <p className="text-xs text-zinc-500">Setor <b>Comercial</b> · todos do time serão lembrados</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="close-sector-modal" className="rounded p-1 text-zinc-400 hover:text-zinc-700"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-600">Título</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex.: Alinhamento semanal do comercial" data-testid="sector-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-600">Data</label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="sector-date-input" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-600">Hora</label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="sector-time-input" />
            </div>
          </div>
          <p className="rounded-lg bg-indigo-50/60 p-3 text-xs text-indigo-700">
            A Isa vai avisar todos do <b>Comercial</b> pelo WhatsApp <b>1 dia antes</b> (mesma hora) e <b>1 hora antes</b>.
          </p>
          <div className="flex justify-end gap-2 border-t border-zinc-100 pt-4">
            <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
            <Btn onClick={submit} disabled={saving} data-testid="save-sector-meeting"><Plus size={16} /> {saving ? "Agendando..." : "Agendar reunião"}</Btn>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default function Agenda() {
  const [events, setEvents] = useState([]);
  const [sectorMeetings, setSectorMeetings] = useState([]);
  const [isManager, setIsManager] = useState(false);
  const [who, setWho] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = () => api.get("/isa/agenda/team", { params: { upcoming: true } })
    .then(({ data }) => {
      setEvents(data.events || []);
      setSectorMeetings((data.sector_meetings || []).map((m) => ({ ...m, kind: "sector_meeting" })));
      setIsManager(!!data.is_manager);
    })
    .catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    if (!window.confirm("Cancelar este compromisso?")) return;
    try { await api.delete(`/isa/agenda/${id}`); toast.success("Cancelado"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const cancelSector = async (id) => {
    if (!window.confirm("Cancelar esta reunião do setor? Os lembretes deixam de ser enviados.")) return;
    try { await api.delete(`/isa/sector-meetings/${id}`); toast.success("Reunião cancelada"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const vendors = useMemo(() => {
    const m = new Map();
    for (const e of events) if (e.employee_id) m.set(e.employee_id, e.employee_name);
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [events]);

  const personal = who === "all" ? events : events.filter((e) => e.employee_id === who);
  // sector meetings always show for everyone (they are company-wide)
  const all = [...sectorMeetings, ...personal];

  const groups = {};
  for (const e of all) {
    const k = dayKey(e.start);
    (groups[k] = groups[k] || []).push(e);
  }
  // sort each day's items by time
  for (const k of Object.keys(groups)) groups[k].sort((a, b) => (a.start || "").localeCompare(b.start || ""));
  const now = new Date();
  const upcoming = all.filter((e) => !e.start || new Date(e.start) >= now).length;
  const meetings = all.filter((e) => e.kind === "meeting" || e.kind === "sector_meeting").length;

  return (
    <div className="space-y-6" data-testid="agenda-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600"><CalendarClock size={20} /></div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-900">Agenda {isManager ? "da equipe" : ""}</h1>
            <p className="text-sm text-zinc-500">{isManager
              ? "Reuniões do setor, compromissos e follow-ups. Peça à Isa no WhatsApp: “agenda reunião do comercial amanhã às 15h”."
              : "Seus compromissos e as reuniões do setor. Peça à Isa: “Agenda uma reunião sexta às 14h”."}</p>
          </div>
        </div>
        {isManager && (
          <Btn onClick={() => setShowCreate(true)} data-testid="new-sector-meeting-btn"><Plus size={16} /> Nova reunião do setor</Btn>
        )}
      </div>

      <div className="flex gap-3">
        <Card className="flex-1 p-4"><p className="text-xs text-muted-foreground">Próximos</p><p className="mt-1 font-display text-2xl font-semibold">{upcoming}</p></Card>
        <Card className="flex-1 p-4"><p className="text-xs text-muted-foreground">Reuniões</p><p className="mt-1 font-display text-2xl font-semibold">{meetings}</p></Card>
        {isManager && <Card className="flex-1 p-4"><p className="text-xs text-muted-foreground">Reuniões de setor</p><p className="mt-1 font-display text-2xl font-semibold">{sectorMeetings.length}</p></Card>}
      </div>

      {isManager && vendors.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="agenda-vendor-filter">
          <button onClick={() => setWho("all")} data-testid="agenda-filter-all"
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${who === "all" ? "bg-indigo-600 text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            Todos
          </button>
          {vendors.map((v) => (
            <button key={v.id} onClick={() => setWho(v.id)} data-testid={`agenda-filter-${v.id}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${who === v.id ? "bg-indigo-600 text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              <User size={12} /> {firstName(v.name)}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Carregando...</p>
      ) : !all.length ? (
        <Card className="p-10 text-center">
          <CalendarClock size={30} className="mx-auto text-muted-foreground" />
          <p className="mt-3 font-medium">Nenhum compromisso à frente</p>
          <p className="mt-1 text-sm text-zinc-500">{isManager ? "Crie uma reunião de setor ou peça à Isa pelo WhatsApp." : "A ISA agenda em linguagem natural pelo WhatsApp."}</p>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groups).map(([day, list]) => (
            <div key={day}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{day}</p>
              <div className="space-y-2">
                {list.map((e) => {
                  const meta = KIND[e.kind] || KIND.reminder;
                  const Icon = meta.icon;
                  const past = e.start && new Date(e.start) < now;
                  const isSector = e.kind === "sector_meeting";
                  return (
                    <Card key={e.id} className={`flex items-center gap-3 p-3 ${past ? "opacity-60" : ""} ${isSector ? "border-indigo-200 bg-indigo-50/30" : ""}`} data-testid={`agenda-event-${e.id}`}>
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600"><Icon size={17} /></span>
                      <div className="min-w-0 flex-1">
                        <p className="flex flex-wrap items-center gap-2 truncate font-medium">{e.title}
                          <Badge tone={meta.tone}>{meta.label}</Badge>
                          {isSector && <Badge tone="muted">Setor · {e.sector_label || "Comercial"}</Badge>}
                          {e.status === "done" && <Badge tone="muted">concluído</Badge>}
                        </p>
                        <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1"><Clock size={12} /> {fmt(e.start)}</span>
                          {isSector && e.participant_count != null ? <span className="inline-flex items-center gap-1 text-indigo-600"><Users size={11} /> {e.participant_count} {e.participant_count === 1 ? "pessoa" : "pessoas"}</span> : null}
                          {!isSector && e.account_name ? <span>· {e.account_name}</span> : null}
                          {!isSector && isManager && e.employee_name ? <span className="inline-flex items-center gap-1 text-indigo-600"><User size={11} /> {firstName(e.employee_name)}</span> : null}
                          {isSector && e.created_by_name ? <span>· por {firstName(e.created_by_name)}</span> : null}
                        </p>
                      </div>
                      {(isSector ? isManager : true) && (
                        <button onClick={() => isSector ? cancelSector(e.id) : cancel(e.id)} data-testid={`cancel-event-${e.id}`} className="rounded p-1.5 text-zinc-400 hover:text-red-600"><Trash2 size={15} /></button>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={load} />}
    </div>
  );
}
